import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'

import {
  CreateLink,
  Link,
  LinkListRequest,
  PaginatedLinksResponse,
  PaginationMeta,
} from '../types/link'
import {
  loadLinksList,
  loadLinksTree,
  loadLinksTreeById,
  modifyLink,
  removeLink,
  saveLink,
} from '../api/link'
import { RootState } from '../../../../redux/store'
import { sortLinks } from '../utils/sortLinks'

interface GetLinkById {
  linkTree: Link[]
}

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 0,
}

const getCurrentParentId = (linksParentsIds: string[]): string | undefined =>
  linksParentsIds.length > 1
    ? linksParentsIds[linksParentsIds.length - 1]
    : undefined

const applyLinksList = (
  state: LinkSliceState,
  payload: PaginatedLinksResponse,
) => {
  state.linksTree = sortLinks(payload.items)
  state.pagination = {
    page: payload.page,
    limit: payload.limit,
    total: payload.total,
    totalPages: payload.totalPages,
  }
  state.activeListRequestId = undefined
  state.loading = false
  state.error = undefined
}

const reloadCurrentLinksPage = async (state: RootState) => {
  return loadLinksList({
    page: state.link.pagination.page || 1,
    limit: state.link.pagination.limit || 25,
    parentId: getCurrentParentId(state.link.linksParentsIds),
  })
}

export const getLinksList = createAsyncThunk(
  'links/getLinksList',
  async (request: LinkListRequest, thunkApi) => {
    try {
      return await loadLinksList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//get linksTree
export const getLinksTree = createAsyncThunk(
  'links/getLinksTree',
  async (_, thunkApi) => {
    try {
      return await loadLinksTree()
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//get linksTreeById
export const getLinksTreeById = createAsyncThunk(
  'links/getLinksTreeById',
  async ({ id }: { id: string }, thunkApi) => {
    try {
      const linkTree = await loadLinksTreeById(id)
      return { linkTree: linkTree?.children || [] }
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//goBackToParentFolder
export const goBackToParentFolder = createAsyncThunk(
  'links/goBackToParentFolder',
  async (_, thunkApi) => {
    try {
      const state = thunkApi.getState() as RootState

      let linksParentsIds = state.link.linksParentsIds
      let linkTree: Link[] = []
      if (linksParentsIds[linksParentsIds.length - 2] === 'root') {
        linkTree = await loadLinksTree()
      } else {
        linkTree =
          (await loadLinksTreeById(linksParentsIds[linksParentsIds.length - 2]))
            ?.children || []
      }

      return linkTree
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//create link
export const addLink = createAsyncThunk(
  'link/addLink',
  async (link: CreateLink, thunkApi) => {
    try {
      await saveLink(link)
      return await reloadCurrentLinksPage(thunkApi.getState() as RootState)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//update link
export const updateLink = createAsyncThunk(
  'link/updateLink',
  async (link: Link, thunkApi) => {
    try {
      await modifyLink(link)
      return await reloadCurrentLinksPage(thunkApi.getState() as RootState)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

//delete link
export const deleteLink = createAsyncThunk(
  'link/deleteLink',
  async (id: string, thunkApi) => {
    try {
      return await removeLink(id)
    } catch (e) {
      return thunkApi.rejectWithValue('step Error')
    }
  },
)

interface LinkSliceState {
  linksTree: Link[]
  linksParentsIds: string[]
  loading: boolean
  pagination: PaginationMeta
  activeListFolderId?: string
  activeListRequestId?: string
  error?: SerializedError
}

const initialState: LinkSliceState = {
  linksTree: [],
  linksParentsIds: ['root'],
  loading: true,
  pagination: emptyPagination,
  activeListFolderId: undefined,
  activeListRequestId: undefined,
  error: undefined,
}

const linkSlice = createSlice({
  name: 'link',
  initialState,
  reducers: {
    setLinksParentsIds: (state, action: PayloadAction<string[]>) => {
      state.linksParentsIds = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getLinksList.fulfilled,
      (
        state,
        action: PayloadAction<PaginatedLinksResponse> & { meta: any },
      ) => {
        if (state.activeListRequestId !== action.meta.requestId) {
          return
        }

        applyLinksList(state, action.payload)
      },
    )
    builder.addCase(getLinksList.pending, (state, action: any) => {
      state.activeListRequestId = action.meta.requestId
      state.activeListFolderId = undefined
      state.linksTree = []
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getLinksList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getLinksTree.fulfilled, (state, action: PayloadAction<Link[]>) => {
      if (state.activeListFolderId !== undefined) {
        return
      }

      state.linksTree = sortLinks(action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getLinksTree.pending, (state) => {
      state.loading = true
      state.linksTree = []
      state.activeListFolderId = undefined
      state.error = undefined
    })
    builder.addCase(getLinksTree.rejected, (state, action) => {
      if (state.activeListFolderId !== undefined) {
        return
      }

      state.loading = false
      state.linksTree = []
      state.error = action.error
    })
    builder.addCase(getLinksTreeById.fulfilled, (
      state,
      action: PayloadAction<GetLinkById> & {
        meta: { arg: { id: string } }
      },
    ) => {
      if (state.activeListFolderId !== action.meta.arg.id) {
        return
      }

      state.linksTree = sortLinks(action.payload.linkTree)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getLinksTreeById.pending, (state, action) => {
      state.loading = true
      state.linksTree = []
      state.activeListFolderId = action.meta.arg.id
      state.error = undefined
    })
    builder.addCase(getLinksTreeById.rejected, (state, action) => {
      if (state.activeListFolderId !== action.meta.arg.id) {
        return
      }

      state.loading = false
      state.linksTree = []
      state.error = action.error
    })
    builder.addCase(goBackToParentFolder.fulfilled, (
      state,
      action: PayloadAction<Link[]>,
    ) => {
      state.linksTree = sortLinks(action.payload)
      state.loading = false
      state.error = undefined
    })
    builder.addCase(goBackToParentFolder.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(goBackToParentFolder.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      addLink.fulfilled,
      (state, action: PayloadAction<PaginatedLinksResponse>) => {
        applyLinksList(state, action.payload)
      },
    )
    builder.addCase(addLink.pending, (state) => {
      state.activeListRequestId = undefined
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(
      updateLink.fulfilled,
      (state, action: PayloadAction<PaginatedLinksResponse>) => {
        applyLinksList(state, action.payload)
      },
    )
    builder.addCase(updateLink.pending, (state) => {
      state.activeListRequestId = undefined
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteLink.fulfilled, (state, action: PayloadAction<string>) => {
      const wasOnCurrentPage = state.linksTree.some(
        (link) => link.id === action.payload,
      )

      state.linksTree = state.linksTree.filter(
        (link) => link.id !== action.payload,
      )

      if (wasOnCurrentPage && state.pagination.total > 0) {
        state.pagination.total -= 1
        state.pagination.totalPages =
          state.pagination.total === 0
            ? 0
            : Math.ceil(state.pagination.total / state.pagination.limit)
      }

      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteLink.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteLink.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export const { setLinksParentsIds } = linkSlice.actions
export default linkSlice.reducer
